// @flow

import * as React from 'react'
import * as mobx from 'mobx'
import PT from 'prop-types'
import R from 'ramda'
import moment from 'moment'
import * as o from 'rxjs/operators'
import logger from '@vimcar/shared-utils/logger'
import { KEY_ESC } from '@vimcar/shared-utils/key-codes'
import { type Params, type InitializerViewModel } from '@vimcar/shared-utils/linkViewModel'
import mkUrn from '@vimcar/shared-stores/mobx/Urn'
import { parseUrn } from '@vimcar/shared-stores/utils/parseUrn'
import { type DomainSwitch } from '@vimcar/shared-stores/mobx/DomainSwitch'
import { type Calendar } from '@vimcar/shared-stores/mobx/Calendar'
import { type Inventory } from '@vimcar/shared-stores/mobx/Calendar/Inventories/Inventory'
import { type Bookings } from '@vimcar/shared-stores/mobx/Calendar/Bookings'
import { type Userinfo } from '@vimcar/shared-stores/mobx/Userinfo'
import { type Users } from '@vimcar/shared-stores/mobx/users'
import { type Localization } from '@vimcar/shared-components/components/LocalizationProvider'
import { type Inventories } from '@vimcar/shared-stores/mobx/Calendar/Inventories'

import { O } from '#utils/rx'
import i18n from '#utils/i18n'
import {
  CALENDAR_VIEW,
  SIMPLIFIED_VIEW,
  WALKTHROUGH_SEEN_NOT_SET,
  SHOW_WALKTHROUGH_TIMEOUT,
} from '#utils/booking'
import { BREAKPOINT_MOBILE } from '#utils/ui'
import styles from './styles.css'

const tp = i18n.prefix('components.CreateBooking')

type Props = {
  history: { push: (string) => void },
  onSetCalendarView: () => void,
  toggleViewWalkThroughSeen: boolean,
  view: string | null,
}

type Context = {
  domain: DomainSwitch<any>,
  calendar: Calendar,
  persistentStore: { values: mobx.ObservableMap<boolean> },
  users: Users,
  userinfo: Userinfo<*>,
  viewport: { is: (string) => boolean },
  localization: Localization,
}

type AvailableVehicle = {
  handleClickBook: () => void,
  licenseplate: string,
  makeModel: string,
  name: string,
  urn: string,
}

type Entry = {
  blocking: boolean,
  end: number,
  id: string,
  label?: string,
  onChange?: ({ start: number, end: number, resourceId: string }) => void,
  onSubmit?: ({ start: number, end: number, resourceId: string }) => void,
  resource: string,
  start: number,
}

type Resource = {
  id: string,
  label: ?string,
  vehicle: Inventory,
}

type LayoverStep = {
  position: string,
  selector: string,
  text: string,
  title: string,
}

type _ViewModel = {|
  _startCalendarViewWalkthrough: ?(key: string, prop: string) => void,
  _startSimpleViewWalkthrough: ?(key: string, prop: string) => void,
  _startWalkThrough: ?(key: string, prop: string) => void,
  _view: string,
  approvalNeeded: boolean,
  availableVehicles: Array<AvailableVehicle>,
  bookableInventories: Array<Inventory>,
  bookingDetailsRequiredFields: Array<string>,
  bookingDialogData: Object | null,
  bookingDialogFormRef: Object | null,
  bookingTimeFrameSelectorRef: { current: null | Object },
  bookings: Bookings,
  calendarViewBookingLayoverSeen: boolean,
  calendarViewBookingLayoverSteps: Array<LayoverStep>,
  calendarViewTimeFrameLayoverSeen: boolean,
  calendarViewTimeFrameLayoverSteps: Array<LayoverStep>,
  changedTimeframe: boolean,
  checkClicked: boolean,
  checkClickedOnce: boolean,
  checkTimeframe: (start: string, end: string) => void,
  commentOnlyFields: Array<string>,
  createBooking: (Object) => void,
  createBookingLoading: boolean,
  end: string | null,
  entries: Array<Entry>,
  error: string | null,
  formInvalidError: boolean,
  getTimeframe: (start: string, end: string) => void,
  handleBookingDialogChange: (Object) => void,
  handleCalendarViewBookingLayover: ({ type: string }) => void,
  handleCalendarViewCloseTimeFrameLayover: ({ action: string }) => void,
  handleChange: ({ start: number, end: number, resourceId: string }) => void,
  handleChangeTimeframe: (start: ?string, end: ?string) => void,
  handleClickBook: (Inventory) => () => void,
  handleClickCancel: () => void,
  handleClickCheck: () => void,
  handleClickConfirm: () => void,
  handleClickMyBookings: () => void,
  handleClickNewBooking: () => void,
  handleClickShowCalendar: () => void,
  handleCreateEntry: ({ start: number, end: number, id: string }) => void,
  handleKeyDown: ({ keyCode: number }) => void,
  handleSimpleViewCloseBookingLayover: ({ action: string }) => void,
  handleSimpleViewCloseTimeFrameLayover: ({ action: string }) => void,
  handleSubmit: () =>
    ({ start: number, end: number, resourceId: string }) => void,
  hasTimeframe: boolean,
  inventories: Inventories,
  inventory: string | null,
  inventoryCalendarRef: Object | null,
  loaded: boolean,
  loadingBookings: boolean,
  newBooking: Object | null,
  noTimeframe: boolean,
  noVehiclesAvailable: boolean,
  preference: Object,
  resources: Array<Resource>,
  setBookingDialogFormRef: (Object | null) => void,
  setBookingWalkThroughDelayTimeout: boolean,
  setInventoryCalendarRef: (Object | null) => void,
  setNewBooking: ({ start: string, end: string, inventory: Object }) => void,
  setSettingsWalkThroughDelayTimeout: boolean,
  showBookingDetailsRequired: boolean,
  showCalendarView: boolean,
  showCalendarViewBookingLayover: boolean,
  showCalendarViewTimeFrameLayover: boolean,
  showCommentOnly: boolean,
  showConfirmDialog: boolean,
  showDoneDialog: boolean,
  showLoader: boolean,
  showNoTimeframeWarning: boolean,
  showOnlySubscibedVehicles: boolean,
  showSimpleViewBookingLayover: boolean,
  showSimpleViewTimeFrameLayover: boolean,
  showSimplifiedView: boolean,
  simpleViewBookingLayoverSeen: boolean,
  simpleViewBookingLayoverSteps: Array<LayoverStep>,
  simpleViewTimeFrameLayoverSeen: boolean,
  simpleViewTimeFrameLayoverSteps: Array<LayoverStep>,
  start: string | null,
  timeframeError: string | null,
  timezone: string,
  validateTimeframe: () => boolean,
  vehiclesAvailable: boolean,
  localization: Localization
|}

export type ViewModel = {
  ...InitializerViewModel<any>,
  ..._ViewModel,
}

const mkViewModel = (
  { registerObservable, registerAutorun }: Params,
  props: Props,
  ctx: Context,
): ViewModel => {
  const defaultTimeframe = getDefaultTimeframe()
  const vm: ViewModel = mobx.observable(({
    _startWalkThrough: mobx.action((key, prop) => {
      if (
        ctx.viewport.is(BREAKPOINT_MOBILE) ||
        ctx.persistentStore.values.get(key) === true
      ) {
        return
      }
      ctx.persistentStore.values.set(key, true)
      vm[prop] = true
    }),

    _startSimpleViewWalkthrough: null,

    _startCalendarViewWalkthrough: null,

    get showCalendarView() {
      return vm._view === CALENDAR_VIEW
    },

    get showSimplifiedView() {
      return vm._view === SIMPLIFIED_VIEW
    },

    get _view() {
      const view = props.view
      return ctx.viewport.is(BREAKPOINT_MOBILE) || !view
        ? SIMPLIFIED_VIEW
        : view
    },

    error: null,

    start: defaultTimeframe.start,

    end: defaultTimeframe.end,

    inventory: null,

    loaded: false,

    loadingBookings: false,

    newBooking: null,

    showDoneDialog: false,

    createBookingLoading: false,

    checkClickedOnce: false,

    checkClicked: false,

    timeframeError: null,

    changedTimeframe: false, // Property for GA tracking

    inventoryCalendarRef: null, // Property for GA tracking

    bookingDialogData: null,

    bookingDialogFormRef: null,

    bookingTimeFrameSelectorRef: React.createRef(),

    formInvalidError: false,

    get preference() {
      return ctx.domain.currentDomain.foxboxPreferences.map || {}
    },

    get timezone() {
      return ctx.localization.zoneinfo
    },

    get localization() {
      return ctx.localization
    },

    simpleViewTimeFrameLayoverSeen: false,

    calendarViewTimeFrameLayoverSeen: false,

    simpleViewBookingLayoverSeen: false,

    calendarViewBookingLayoverSeen: false,

    get approvalNeeded() {
      const userGroups = ctx.userinfo.permissions.getActionableResourcesUrns(
        'own',
        'system_group',
      )
      const isBookingManager = userGroups.some((groupUrn) => {
        const { uuid } = parseUrn(groupUrn)
        return uuid === 'bookingmanager'
      })

      return Boolean(
        ctx.domain.currentDomain.settings.map &&
        ctx.domain.currentDomain.settings.map.booking.bookingApproval &&
        !isBookingManager,
      )
    },

    get inventories() {
      const inventories = ctx.calendar.inventories
      if (!inventories) throw new Error('Inventories not defined')
      return inventories
    },

    get bookings() {
      const bookings = ctx.calendar.bookings
      if (!bookings) throw new Error('Bookings not defined')
      return bookings
    },

    get showOnlySubscibedVehicles() {
      return Boolean(
        ctx.domain.currentDomain.settings.map &&
        !ctx.domain.currentDomain.settings.map
          .booking.unsubscribedCarsVisibleBooking
      )
    },

    get bookableInventories() {
      return vm.showOnlySubscibedVehicles
        ? vm.inventories.bookableAccessible
        : vm.inventories.bookable
    },

    get availableVehicles() {
      return vm.bookableInventories
        .filter((inventory) => vm.bookings.all.all
          .concat(vm.bookings.all.pending)
          .every((booking) =>
            inventory.urn.id !== booking.inventoryUrn.id ||
              (
                inventory.urn.id === booking.inventoryUrn.id &&
                booking.duration.start.isAfter(vm.end) ||
                booking.duration.end.isBefore(vm.start)
              )
          )
        )
        .map((inventory) => ({
          handleClickBook: vm.handleClickBook(inventory),
          licenseplate: inventory.details.licenseplate,
          makeModel: inventory && inventory.details
            ? `${inventory.details.make} ${inventory.details.model}`
            : '',
          name: inventory.name,
          urn: inventory.urn.urnString,
        }))
    },

    get entries() {
      const selfUrn = ctx.users.optionalSelf.get().urn
      const entries = vm.bookings.all.all
        .concat(vm.bookings.all.pending)
        .map((booking) => ({
          id: booking.urn.urnString,
          blocking: true,
          label: (
            booking.owner &&
            (
              booking.owner.urn.urnString === selfUrn ||
              vm.preference.booking_owner_visible === 'true'
            )
          )
            ? `${booking.owner.firstname} ${booking.owner.lastname}`
            : undefined,
          start: +moment.utc(booking.duration.start.iso),
          end: +moment.utc(booking.duration.end.iso),
          resource: booking.inventoryUrn.urnString,
          comment: (
            booking.owner &&
            (
              booking.owner.urn.urnString === selfUrn ||
              vm.preference.booking_comment_visible === 'true'
            )
          )
            ? booking.comment
            : undefined,
        }))

      if (vm.hasTimeframe && vm.checkClickedOnce) {
        const initialVehicleUrn =
          vm.inventory ||
          vm.availableVehicles[0] && vm.availableVehicles[0].urn ||
          vm.bookableInventories[0].urn.urnString


        const newBooking = {
          id: 'new booking',
          blocking: true,
          start: +moment.utc(vm.start),
          end: +moment.utc(vm.end),
          resource: initialVehicleUrn,
          onChange: vm.handleChange,
          onSubmit: vm.handleSubmit(),
        }
        entries.push(newBooking)
      }

      return entries
    },

    get hasTimeframe() {
      return vm.start !== null && vm.end !== null
    },

    get noTimeframe() {
      return vm.start === null || vm.end === null
    },

    get noVehiclesAvailable() {
      return (
        vm.loaded &&
        vm.availableVehicles.length === 0
      )
    },

    get resources() {
      return vm.bookableInventories
        .map((inventory) => ({
          id: inventory.urn.urnString,
          label: inventory.details.licenseplate,
          vehicle: inventory,
        }))
    },

    get showConfirmDialog() {
      return !!vm.newBooking
    },

    get showLoader() {
      return vm.loadingBookings
    },

    get showNoTimeframeWarning() {
      return (
        vm.showSimplifiedView &&
        vm.noTimeframe
      )
    },

    get vehiclesAvailable() {
      return (
        !vm.noTimeframe &&
        vm.loaded &&
        vm.availableVehicles.length > 0
      )
    },

    commentOnlyFields: mobx.observable.ref(['comment']),

    bookingDetailsRequiredFields: mobx.observable.ref([
      'contactcompany',
      'contactname',
      'reason',
      'comment',
    ]),

    get showBookingDetailsRequired() {
      return R.pathEq(
        ['booking_details_required'],
        'true',
        vm.preference
      )
    },

    get showCommentOnly() {
      return !vm.showBookingDetailsRequired
    },

    get showSimpleViewTimeFrameLayover() {
      return (
        props.toggleViewWalkThroughSeen !== WALKTHROUGH_SEEN_NOT_SET &&
        !vm.simpleViewTimeFrameLayoverSeen &&
        vm.showSimplifiedView &&
        !!vm._startSimpleViewWalkthrough
      )
    },

    get simpleViewTimeFrameLayoverSteps() {
      return [{
        title: tp('simplified-view-choose-timeframe-title'),
        text: tp('simplified-view-choose-timeframe-text'),
        position: 'bottom',
        selector: `.${styles.timeframe}`,
      }]
    },

    get showSimpleViewBookingLayover() {
      return (
        vm.simpleViewTimeFrameLayoverSeen &&
        !vm.simpleViewBookingLayoverSeen &&
        vm.checkClicked
      )
    },

    get simpleViewBookingLayoverSteps() {
      return [{
        title: tp('simplified-view-booking-explanation-title'),
        text: tp('simplified-view-booking-explanation-text'),
        position: 'bottom-left',
        selector: `.${styles.availableVehiclesList}`,
      }]
    },

    get showCalendarViewTimeFrameLayover() {
      return (
        props.toggleViewWalkThroughSeen !== WALKTHROUGH_SEEN_NOT_SET &&
        !vm.calendarViewTimeFrameLayoverSeen &&
        vm.showCalendarView &&
        // $FlowFixMe
        vm._startCalendarViewWalkthrough
      )
    },

    get calendarViewTimeFrameLayoverSteps() {
      return [{
        title: tp('calender-view-choose-timeframe-title'),
        text: tp('calender-view-choose-timeframe-text'),
        position: 'bottom',
        selector: `.${styles.timeframe}`,
      }]
    },

    get showCalendarViewBookingLayover() {
      return (
        vm.calendarViewTimeFrameLayoverSeen &&
        !vm.calendarViewBookingLayoverSeen &&
        vm.checkClicked
      )
    },

    get calendarViewBookingLayoverSteps() {
      return [{
        title: tp('calendar-view-booking-explanation-title-step1'),
        text: tp('calendar-view-booking-explanation-text-step1'),
        position: 'top-left',
        selector: `.${styles.calendar}`,
      }, {
        title: tp('calendar-view-booking-explanation-title-step2'),
        text: tp('calendar-view-booking-explanation-text-step2'),
        position: 'top-left',
        selector: `.${styles.walkthrough}`,
      }, {
        title: tp('calendar-view-booking-explanation-title-step3'),
        text: tp('calendar-view-booking-explanation-text-step3'),
        position: 'top-left',
        selector: `.${styles.calendar}`,
      }, {
        title: tp('calendar-view-booking-explanation-title-step4'),
        text: tp('calendar-view-booking-explanation-text-step4'),
        position: 'top-left',
        selector: `.${styles.walkthrough}`,
      }]
    },

    checkTimeframe: mobx.action((start, end) => {
      vm.loadingBookings = true
      registerObservable(
        ctx.calendar.listInTimeframe(start, end).pipe(
          o.catchError(mobx.action((err) => {
            logger.error(err)
            vm.loadingBookings = false
            vm.error = tp('failed-to-get-bookings')
            return O.empty()
          })),
          o.tap(mobx.action(() => {
            vm.loaded = true
            vm.loadingBookings = false
            vm.error = null
          }))
        )
      )
    }),

    createBooking: mobx.action((booking) => {
      if (
        vm.bookingDialogData === false ||
        (
          vm.bookingDialogData === null &&
          vm.showBookingDetailsRequired
        ) ||
        (
          vm.bookingDialogFormRef &&
          !vm.bookingDialogFormRef.validate()
        )
      ) return
      if (
        vm.bookingDialogData &&
        !vm.bookingDialogData.contactcompany &&
        !vm.bookingDialogData.contactname &&
        vm.showBookingDetailsRequired
      ) {
        vm.formInvalidError = true
        return
      }
      vm.createBookingLoading = true
      registerObservable(
        vm.bookings.create({
          ...booking,
          duration: {
            ...booking.duration,
          },
        }).pipe(
          o.catchError(mobx.action((err) => {
            logger.error(err)
            vm.error = err.status === 409
              ? tp('conflict-error')
              : tp('failed-to-create-booking')
            vm.createBookingLoading = false
            return O.empty()
          })),
          o.tap(mobx.action(() => {
            const newTimeframe = getDefaultTimeframe()
            vm.changedTimeframe = false
            vm.checkClickedOnce = false
            vm.createBookingLoading = false
            vm.start = newTimeframe.start
            vm.end = newTimeframe.end
            vm.newBooking = null
            vm.error = null
            vm.showDoneDialog = true
            vm.loaded = false
            vm.inventory = null
            const selectorRef = vm.bookingTimeFrameSelectorRef
            if (selectorRef && selectorRef.current) {
              selectorRef.current.viewModel.end = vm.end
              selectorRef.current.viewModel.start = vm.start
            }
          }))
        )
      )
    }),

    getTimeframe: mobx.action((start, end) => {
      registerObservable(
        ctx.calendar.listInTimeframe(start, end).pipe(
          o.catchError(mobx.action((err) => {
            logger.error(err)
            vm.error = tp('failed-to-get-bookings')
            return O.empty()
          })),
          o.tap(mobx.action(() => {
            vm.error = null
          }))
        )
      )
    }),

    handleClickBook: (inventory) => mobx.action(() => {
      vm.setNewBooking({
        start: vm.start || '',
        end: vm.end || '',
        inventory: inventory.urn,
      })
    }),

    handleChange: mobx.action(({ start, end, resourceId }) => {
      const isoStart = moment.utc(start).format()
      const isoEnd = moment.utc(end).format()
      if (isoStart !== vm.start || isoEnd !== vm.end) {
        vm.changedTimeframe = true
      }
      vm.inventory = resourceId
      vm.handleChangeTimeframe(isoStart, isoEnd)
      const selectorRef = vm.bookingTimeFrameSelectorRef
      if (selectorRef && selectorRef.current) {
        selectorRef.current.viewModel.end = vm.end
        selectorRef.current.viewModel.start = vm.start
      }
    }),

    handleSubmit: () =>
      mobx.action(({ start, end, resourceId }) => {
        const inventoryUrn = mkUrn({ input: resourceId })
        vm.setNewBooking({
          start: moment.utc(start).format(),
          end: moment.utc(end).format(),
          inventory: inventoryUrn,
        })
      }),

    handleChangeTimeframe: mobx.action((start, end) => {
      vm.start = start
      vm.end = end

      if (vm.showSimplifiedView) {
        vm.checkClickedOnce = false
        vm.loaded = false
      }
      if (vm.checkClickedOnce || vm.timeframeError) {
        vm.validateTimeframe()
      }
    }),

    handleClickCancel: mobx.action(() => {
      vm.newBooking = null
      vm.checkClickedOnce = false
    }),

    setBookingWalkThroughDelayTimeout: false,

    handleClickCheck: mobx.action(() => {
      if (vm.hasTimeframe && vm.validateTimeframe()) {
        vm.inventory = null
        vm.checkTimeframe(vm.start || '', vm.end || '')
        vm.checkClickedOnce = true
        if (
          !vm.setBookingWalkThroughDelayTimeout &&
    !ctx.viewport.is(BREAKPOINT_MOBILE)
        ) {
          vm.setBookingWalkThroughDelayTimeout = window.setTimeout(
            mobx.action(() => {
              vm.checkClicked = true
            }),
            SHOW_WALKTHROUGH_TIMEOUT
          )
        }
      }
    }),

    handleClickConfirm: mobx.action(() => {
      vm.createBooking(
        R.merge(
          R.pick(
            ['duration', 'inventory'],
            vm.newBooking
          ),
          vm.bookingDialogData
        )
      )
    }),

    setSettingsWalkThroughDelayTimeout: false,

    handleClickMyBookings: mobx.action(() => {
      props.history.push('/dashboard/bookings')
    }),

    handleClickNewBooking: mobx.action(() => {
      vm.loaded = false
      vm.loadingBookings = false
      vm.showDoneDialog = false
    }),

    handleClickShowCalendar: mobx.action(() => {
      props.onSetCalendarView()
    }),

    handleCreateEntry: mobx.action(({ start, end, id }) => {
      vm.start = moment.utc(start).format()
      vm.end = moment.utc(end).format()
      vm.checkClickedOnce = true
      vm.inventory = id
      vm.loaded = true
      const selectorRef = vm.bookingTimeFrameSelectorRef
      if (selectorRef && selectorRef.current) {
        selectorRef.current.viewModel.end = vm.end
        selectorRef.current.viewModel.start = vm.start
      }
    }),

    handleBookingDialogChange: mobx.action((data) => {
      vm.bookingDialogData = R.filter(R.complement(R.isNil), data)
      vm.formInvalidError = false
    }),

    handleSimpleViewCloseTimeFrameLayover: mobx.action((callback) => {
      if (R.pathEq(['action'], 'close', callback)) {
        vm.simpleViewTimeFrameLayoverSeen = true
      }
    }),

    handleSimpleViewCloseBookingLayover: mobx.action((callback) => {
      if (R.pathEq(['action'], 'close', callback)) {
        vm.simpleViewBookingLayoverSeen = true
      }
    }),

    handleCalendarViewCloseTimeFrameLayover: mobx.action((callback) => {
      if (R.pathEq(['action'], 'close', callback)) {
        vm.calendarViewTimeFrameLayoverSeen = true
      }
    }),

    handleCalendarViewBookingLayover: mobx.action((callback) => {
      if (R.pathEq(['type'], 'finished', callback)) {
        vm.calendarViewBookingLayoverSeen = true
      }
    }),

    setInventoryCalendarRef: mobx.action((ref) => {
      vm.inventoryCalendarRef = ref
    }),

    setNewBooking: mobx.action(({ start, end, inventory }) => {
      const existingInventory = vm.inventories.map.get(inventory.id)

      vm.newBooking = {
        duration: { start, end },
        formattedStart: ctx.localization.formatDateTime(start),
        formattedEnd: ctx.localization.formatDateTime(end),
        inventory: inventory.urnString,
        get makeModel() {
          return existingInventory
            ? [
              existingInventory.details.make,
              existingInventory.details.model,
            ].join(' ')
            : ''
        },
        get licenseplate() {
          return existingInventory
            ? existingInventory.details.licenseplate
            : ''
        },
      }
    }),

    setBookingDialogFormRef: mobx.action((form) => {
      vm.bookingDialogFormRef = form
    }),

    validateTimeframe: mobx.action(() => {
      if (
        !(vm.start && vm.end) ||
        +moment.utc(vm.start) < +moment.utc(vm.end)
      ) {
        vm.timeframeError = null
        return true
      } else {
        vm.timeframeError = tp('validation-error-start-before-end')
        vm.checkClickedOnce = false
        return false
      }
    }),

    handleKeyDown: mobx.action((event) => {
      if (event.keyCode === KEY_ESC) {
        vm.checkClickedOnce = false
      }
    }),

  }: _ViewModel))

  registerAutorun(() => {
    if (vm.showSimplifiedView && vm._startWalkThrough) {
      vm._startWalkThrough(
        'didSimpleViewTour',
        '_startSimpleViewWalkthrough'
      )
    }
    if (vm.showCalendarView && vm._startWalkThrough) {
      vm._startWalkThrough(
        'didCalendarViewTour',
        '_startCalendarViewWalkthrough'
      )
    }
  })

  return vm
}

const getDefaultTimeframe = () => {
  const now = moment()
  const remainder = 30 - (now.minute() % 30)
  const start = now
    .add(remainder, 'minute')
    .startOf('minute')
  const end = moment(start).add(1, 'hour')

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  }
}

mkViewModel.contextTypes = {
  calendar: PT.object,
  domain: PT.object,
  localization: PT.object.isRequired,
  persistentStore: PT.object,
  users: PT.object,
  userinfo: PT.object,
  viewport: PT.object,
}

export default mkViewModel
